/**
 * TanStack Query helpers over the typed client.
 *
 *   const { data, isPending, error } = useApi("/market/overview");
 *   const runs = useApi("/backtest/runs/{run_id}", { path: { run_id: 52 } });
 *
 * The query key is [path, path params, query params], so pages can invalidate
 * with `queryClient.invalidateQueries({ queryKey: ["/me/watchlists"] })`.
 */
import { queryOptions, useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { api, type OptionsArg, type PathsFor, type RequestOptions, type ResponseOf } from "./client";
import type { paths } from "./schema";

type GetOp<P extends PathsFor<"get">> = NonNullable<paths[P]["get"]>;

export function apiQuery<P extends PathsFor<"get">>(
  path: P,
  ...[options]: OptionsArg<GetOp<P>>
) {
  const opts = (options ?? {}) as RequestOptions<GetOp<P>>;
  return queryOptions({
    queryKey: [path, opts.path ?? null, opts.query ?? null] as const,
    queryFn: ({ signal }) =>
      (api.get as (p: P, o: object) => Promise<ResponseOf<GetOp<P>>>)(path, { ...opts, signal }),
  });
}

type Extra<P extends PathsFor<"get">> = Omit<
  UseQueryOptions<ResponseOf<GetOp<P>>, Error, ResponseOf<GetOp<P>>, readonly unknown[]>,
  "queryKey" | "queryFn"
>;

export function useApi<P extends PathsFor<"get">>(
  path: P,
  options?: RequestOptions<GetOp<P>>,
  extra?: Extra<P>,
) {
  const base = (apiQuery as (p: P, o?: object) => ReturnType<typeof apiQuery<P>>)(path, options);
  return useQuery({ ...base, ...extra } as typeof base);
}
